import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { doubleTapProps } from '@/shared/device/doubleTap';
import type { Device, DeviceScope } from '@/shared/device/device';
import { useDevice } from '@/shared/device/useDevice';
import { DeviceSwitch } from '@/shared/device/DeviceSwitch';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '@/shared/brand/useBrand';
import { useOptionalEditor } from '@/shared/editing/EditorContext';
import { useI18n } from '@/shared/i18n/useT';
import { Icon } from '@/shared/components/ui/Icon';
import { Caret } from '@/shared/components/ui/Caret';
import { SummaryCardsProvider, useOptionalSummaryCards } from './SummaryCardsContext';
import { GridConfigContext } from './GridConfigProvider';
import { GRID_SETTING_KEYS } from './settings';
import { DEFAULT_TABS_PLACEMENT, type TabsPlacement } from './types';
import { useIsMobile } from './ToolbarOverflow';
import { useNoPullToRefresh } from '@/shared/hooks/useNoPullToRefresh';
import { useTopBarMenuHosted, useTopBarMenuSlot } from '@/shared/components/topBarMenuSlot';

type MenuPosition = { left: number; top?: number; bottom?: number; maxHeight?: number }

export type { TabsPlacement };

export interface GridPageTab {
  readonly href: string;
  /** i18n key, never a literal. */
  readonly labelKey: string;
  readonly hidden?: boolean;
  readonly isTextTab?: boolean;
  /** A pivot table or a reconciliation: its rows are a configuration, so there is nothing to export or restore. */
  readonly isPivotTab?: boolean;
  /** Cells holding a value their column's type rejects; a badge shows when above 0. */
  readonly errorCount?: number;
}

export interface GridPageTabMenuLabels {
  readonly copyTo: string;
  readonly delete: string;
  readonly downloadXlsx: string;
  readonly downloadDocx?: string | undefined;
  readonly duplicate: string;
  /** Child of `duplicate`: copies the sheet with its rows. */
  readonly duplicateWithData?: string | undefined;
  /** Child of `duplicate`: copies only the header row. */
  readonly duplicateHeaders?: string | undefined;
  readonly hide: string;
  readonly rename: string;
  readonly sheetList: string;
  readonly placement?: string | undefined;
  readonly placementTop?: string | undefined;
  readonly placementBottom?: string | undefined;
  readonly placementLeft?: string | undefined;
  readonly trash?: string | undefined;
  readonly showErrors?: string | undefined;
  /** Badge tooltip; `{count}` is replaced. */
  readonly errorCount?: string | undefined;
}

/**
 * An extra entry the page adds to the top of every sheet tab menu, so things
 * that belong to the sheet — its views, its automations — live beside the
 * sheet's own options instead of crowding the toolbar.
 */
export interface GridPageTabMenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon?: ReactNode | undefined;
  /** Renders as a radio entry with a check when true. */
  readonly selected?: boolean | undefined;
  readonly onSelect?: (() => void) | undefined;
  /** Nested entries, opened as a flyout when the row is hovered. */
  readonly items?: readonly GridPageTabMenuItem[] | undefined;
  /** Shown above the nested entries, e.g. a device switch saying which device they set. */
  readonly itemsHeader?: ReactNode | undefined;
}

export interface GridPageOverflowLabels {
  readonly previous: string;
  readonly next: string;
}

import GridContextMenu, { type GridMenuItem } from './GridContextMenu';

export interface GridSummaryCardItem {
  readonly id?: string | undefined;
  readonly label: string;
  readonly value: ReactNode;
  readonly rawValue?: string | number | undefined;
  readonly className?: string | undefined;
  readonly onClick?: (() => void) | undefined;
  readonly onDoubleClick?: (() => void) | undefined;
  readonly onEdit?: (() => void) | undefined;
  readonly onDelete?: (() => void) | undefined;
  readonly onMove?: ((placement: 'top' | 'topBelow' | 'bottom' | 'sidebar') => void) | undefined;
}

export function GridSummaryCard({
  label,
  value,
  className = '',
  selected = false,
  onClick,
  onDoubleClick,
  onContextMenu,
  onKebabClick,
}: {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly className?: string | undefined;
  readonly selected?: boolean | undefined;
  readonly onClick?: (() => void) | undefined;
  readonly onDoubleClick?: (() => void) | undefined;
  readonly onContextMenu?: ((event: React.MouseEvent) => void) | undefined;
  readonly onKebabClick?: ((event: React.MouseEvent) => void) | undefined;
}) {
  const content = (
    <div className={`min-w-0 flex-1 ${selected ? 'pr-4' : ''}`}>
      <p className="truncate text-xs text-muted sm:text-xs select-none">{label}</p>
      <p className="mt-0.5 truncate text-sm font-semibold text-fg sm:text-sm select-none">{value}</p>
    </div>
  );

  return (
    <div
      role={onDoubleClick || onClick ? 'button' : undefined}
      tabIndex={onDoubleClick || onClick ? 0 : undefined}
      onClick={onClick}
      {...doubleTapProps(onDoubleClick ? (e) => {
        e.preventDefault();
        e.stopPropagation();
        onDoubleClick();
      } : undefined)}
      onContextMenu={onContextMenu}
      className={`nt-summary relative cursor-pointer text-left transition-colors hover:border-focus hover:bg-surface-hover active:bg-surface-3 select-none flex items-center justify-between ${
        selected ? 'ring-2 ring-accent border-accent bg-surface-2' : ''
      } ${className ?? ''}`.trim()}
    >
      {content}
      {selected && onKebabClick && (
        <button
          type="button"
          aria-label="Card options"
          onClick={(e) => {
            e.stopPropagation();
            onKebabClick(e);
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-muted hover:bg-surface-hover hover:text-fg transition-colors"
        >
          <Icon name="moreVertical" className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function GridSummaryCards({
  cards,
  className = '',
  onAddCard,
  addCardLabel = '+ Add Card',
  vertical = false,
}: {
  readonly cards: readonly GridSummaryCardItem[];
  readonly className?: string | undefined;
  readonly onAddCard?: (() => void) | undefined;
  readonly addCardLabel?: string | undefined;
  readonly vertical?: boolean | undefined;
}) {
  const { t } = useI18n();
  const summaryCardsContext = useOptionalSummaryCards();
  const [selectedCardId, setSelectedCardId] = useState<string | number | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; card: GridSummaryCardItem } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutside = (e: MouseEvent | PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSelectedCardId(null);
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []);

  if (cards.length === 0 && !onAddCard) return null;

  const handleContextMenu = (e: React.MouseEvent, card: GridSummaryCardItem) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, card });
  };

  const menuItems: GridMenuItem[] = contextMenu ? [
    {
      kind: 'action',
      key: 'copy',
      label: t('summaryCards.copyValue'),
      onSelect: () => {
        const textToCopy = contextMenu.card.rawValue !== undefined
          ? String(contextMenu.card.rawValue)
          : typeof contextMenu.card.value === 'string' || typeof contextMenu.card.value === 'number'
            ? String(contextMenu.card.value)
            : contextMenu.card.label;
        void navigator.clipboard.writeText(textToCopy);
      },
    },
    ...(contextMenu.card.onEdit || contextMenu.card.onDoubleClick ? [{
      kind: 'action' as const,
      key: 'edit',
      label: t('summaryCards.editCard'),
      onSelect: () => {
        (contextMenu.card.onEdit ?? contextMenu.card.onDoubleClick)?.();
      },
    }] : []),
    {
      kind: 'submenu',
      key: 'move',
      label: t('grid.menu.move'),
      items: [
        {
          kind: 'action',
          key: 'move-top',
          label: t('summaryCards.placementTop'),
          onSelect: () => {
            if (contextMenu.card.onMove) contextMenu.card.onMove('top');
            else summaryCardsContext?.setSummaryCardsPlacement('top');
          },
        },
        {
          kind: 'action',
          key: 'move-top-below',
          label: t('summaryCards.placementTopBelow'),
          onSelect: () => {
            if (contextMenu.card.onMove) contextMenu.card.onMove('topBelow');
            else summaryCardsContext?.setSummaryCardsPlacement('topBelow');
          },
        },
        {
          kind: 'action',
          key: 'move-bottom',
          label: t('summaryCards.placementBottom'),
          onSelect: () => {
            if (contextMenu.card.onMove) contextMenu.card.onMove('bottom');
            else summaryCardsContext?.setSummaryCardsPlacement('bottom');
          },
        },
        {
          kind: 'action',
          key: 'move-sidebar',
          label: t('summaryCards.placementSidebar'),
          onSelect: () => {
            if (contextMenu.card.onMove) contextMenu.card.onMove('sidebar');
            else summaryCardsContext?.setSummaryCardsPlacement('sidebar');
          },
        },
      ],
    },
    ...(contextMenu.card.onDelete ? [{
      kind: 'action' as const,
      key: 'delete',
      label: t('summaryCards.deleteCard'),
      onSelect: () => {
        contextMenu.card.onDelete?.();
      },
    }] : []),
  ] : [];

  return (
    <>
      <div
        ref={containerRef}
        className={vertical ? `flex flex-col items-stretch gap-2 ${className ?? ''}`.trim() : `flex flex-nowrap items-center gap-2 ${className ?? ''}`.trim()}
      >
        {cards.map((card, index) => {
          const cardKey = card.id ?? index;
          const isSelected = selectedCardId === cardKey;
          return (
            <GridSummaryCard
              key={cardKey}
              label={card.label}
              value={card.value}
              className={card.className}
              selected={isSelected}
              onClick={() => {
                setSelectedCardId(cardKey);
                card.onClick?.();
              }}
              onDoubleClick={card.onDoubleClick ?? card.onEdit}
              onContextMenu={(e) => handleContextMenu(e, card)}
              onKebabClick={card.onEdit || card.onDoubleClick ? () => {
                (card.onEdit ?? card.onDoubleClick)?.();
              } : undefined}
            />
          );
        })}
        {onAddCard && (
          <button
            type="button"
            onClick={onAddCard}
            title={addCardLabel}
            aria-label={addCardLabel}
            className={`nt-summary ${vertical ? 'h-9 w-full' : 'h-[38px] min-w-[38px] mr-1.5 sm:mr-2'} cursor-pointer items-center justify-center border-dashed border-border px-2 text-xs font-semibold text-muted transition-colors hover:border-brand hover:text-brand hover:bg-surface-hover`}
          >
            <span className="flex items-center justify-center gap-0.5 select-none">
              <span aria-hidden="true" className="text-xs font-bold leading-none">+</span>
              <span aria-hidden="true" className="text-sm font-semibold leading-none">Σ</span>
            </span>
          </button>
        )}
      </div>

      {contextMenu && (
        <GridContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={menuItems}
          onClose={() => setContextMenu(null)}
          label={t('summaryCards.title')}
        />
      )}
    </>
  );
}

export interface GridPageLayoutProps {
  readonly children: ReactNode;
  /** Already translated by the caller, which knows its own namespace. */
  readonly title?: string | undefined;
  /** When set, the header shows navigable tabs instead of `title`. */
  readonly tabs?: readonly GridPageTab[] | undefined;
  /** `href` of the active tab. */
  readonly activeTab?: string | undefined;
  readonly onTabChange?: ((tab: GridPageTab) => void) | undefined;
  /** Double-clicking a tab enters inline rename mode. */
  readonly onTabRename?: ((tab: GridPageTab, name: string) => void | Promise<void>) | undefined;
  readonly onTabDelete?: ((tab: GridPageTab) => void | Promise<void>) | undefined;
  readonly onTabDuplicate?: ((tab: GridPageTab) => void | Promise<void>) | undefined;
  readonly onTabDuplicateHeaders?: ((tab: GridPageTab) => void | Promise<void>) | undefined;
  /** A per-tab entry about its shared columns (link with another sheet, or unlink), shown after Duplicate. */
  readonly tabColumnsMenuItem?: ((tab: GridPageTab) => GridPageTabMenuItem | null) | undefined;
  /** Opens the caller's organization-scoped destination chooser for this tab. */
  readonly onTabCopyTo?: ((tab: GridPageTab) => void) | undefined;
  /** Exports text tab in Word .docx format. */
  readonly onTabDownloadDocx?: ((tab: GridPageTab) => void | Promise<void>) | undefined;
  /** Filters the tab's grid to the rows with errors (menu item and error badge). */
  readonly onTabShowErrors?: ((tab: GridPageTab) => void) | undefined;
  readonly onTabHide?: ((tab: GridPageTab) => void | Promise<void>) | undefined;
  readonly onTabShow?: ((tab: GridPageTab) => void | Promise<void>) | undefined;
  readonly onTabOpenTrash?: ((tab: GridPageTab) => void) | undefined;
  /** Reorders tabs when dragged left/right in top bar or up/down in vertical sidebar. */
  readonly onTabReorder?: ((tabs: readonly GridPageTab[], fromIndex: number, toIndex: number) => void | Promise<void>) | undefined;
  /** Configurable placement of sheet tabs: 'top' | 'bottom' | 'left' */
  readonly tabsPlacement?: TabsPlacement | undefined;
  readonly onTabsPlacementChange?: ((placement: TabsPlacement) => void) | undefined;
  readonly tabMenuLabels?: GridPageTabMenuLabels | undefined;
  /** Caller-supplied entries shown at the top of the sheet tab menu. */
  readonly tabMenuItems?: readonly GridPageTabMenuItem[] | undefined;
  readonly overflowLabels?: GridPageOverflowLabels | undefined;
  /** Adds a standard Sheet tab beside the page's existing tabs. */
  readonly onAddTab?: (() => void) | undefined;
  readonly addTabLabel?: string | undefined;
  /** Adds a text document sheet tab. */
  readonly onAddTextTab?: (() => void) | undefined;
  readonly addTextTabLabel?: string | undefined;
  readonly onAddPivotTab?: (() => void) | undefined;
  readonly addPivotTabLabel?: string | undefined;
  readonly onAddReconciliationTab?: (() => void) | undefined;
  readonly addReconciliationTabLabel?: string | undefined;
  /** "Add new sheet with same columns as…": opens the caller's dialog. */
  readonly onAddLinkedTab?: (() => void) | undefined;
  readonly addLinkedTabLabel?: string | undefined;
  readonly addTabDisabled?: boolean | undefined;
  readonly actions?: ReactNode | undefined;
  /** Structured summary card items rendered beside tab navigation (optional). */
  readonly summaryCards?: readonly GridSummaryCardItem[] | undefined;
  readonly onAddSummaryCard?: (() => void) | undefined;
  readonly addSummaryCardLabel?: string | undefined;
  /** Summary cards rendered beside tab navigation on tabbed pages. */
  readonly cards?: ReactNode | undefined;
  /** Actions rendered beside tab navigation on tabbed pages. */
  readonly buttons?: ReactNode | undefined;
  /** Replaces the whole header when a screen needs something other than a title. */
  readonly header?: ReactNode | undefined;
  readonly error?: string | null | undefined;
  readonly className?: string | undefined;
  /** Classes for the area holding the grid itself. */
  readonly contentClassName?: string | undefined;
  /** Let grid screens extend through the shell's content padding. */
  readonly fullBleed?: boolean | undefined;
  /** A panel docked on the right of the content, like the Historial. */
  readonly rightSidebar?: ReactNode;
}

function HeaderOverflowControls({
  labels,
  overflow,
  onPrevious,
  onNext,
}: {
  readonly labels: GridPageOverflowLabels | undefined;
  readonly overflow: { readonly previous: boolean; readonly next: boolean };
  readonly onPrevious: () => void;
  readonly onNext: () => void;
}) {
  if (!labels || (!overflow.previous && !overflow.next)) return null;
  return (
    <div className="nt-header-overflow-controls" aria-hidden="false">
      <button type="button" aria-label={labels.previous} disabled={!overflow.previous} onClick={onPrevious} className="nt-header-overflow-button" tabIndex={overflow.previous ? 0 : -1}>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m14 6-6 6 6 6" /></svg>
      </button>
      <button type="button" aria-label={labels.next} disabled={!overflow.next} onClick={onNext} className="nt-header-overflow-button" tabIndex={overflow.next ? 0 : -1}>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m10 6 6 6-6 6" /></svg>
      </button>
    </div>
  );
}

/**
 * The frame every grid screen shares: header, actions, an error strip and the
 * grid area. Ported from TMS `src/components/GridPageLayout.tsx`.
 *
 * The error strip lives here rather than in each page because a failed save has
 * to stay visible while the grid below it remains usable — a screen that
 * replaces its content with an error message loses whatever the user had
 * selected.
 *
 * Colours are tokens, and the tab title comes from the caller already
 * translated: this component renders no user-visible string of its own.
 */
function GridPageLayoutInner({
  children,
  title,
  tabs,
  activeTab,
  onTabChange,
  onTabRename,
  onTabDelete,
  onTabDuplicate,
  onTabDuplicateHeaders,
  tabColumnsMenuItem,
  onTabCopyTo,
  onTabDownloadDocx,
  onTabShowErrors,
  onTabHide,
  onTabShow,
  onTabOpenTrash,
  onTabReorder,
  tabsPlacement: propTabsPlacement,
  onTabsPlacementChange,
  tabMenuLabels,
  tabMenuItems,
  overflowLabels,
  onAddTab,
  addTabLabel,
  onAddTextTab,
  addTextTabLabel,
  onAddPivotTab,
  addPivotTabLabel,
  onAddReconciliationTab,
  addReconciliationTabLabel,
  onAddLinkedTab,
  addLinkedTabLabel,
  addTabDisabled = false,
  actions,
  summaryCards,
  onAddSummaryCard: onAddSummaryCardProp,
  addSummaryCardLabel,
  cards,
  buttons,
  header,
  error,
  className = '',
  contentClassName = '',
  fullBleed = false,
  rightSidebar,
}: GridPageLayoutProps) {
  const brand = useBrand();
  const summaryCardsContext = useOptionalSummaryCards();
  const summaryCardsVisible = summaryCardsContext?.summaryCardsVisible ?? false;
  const setHasSummaryCards = summaryCardsContext?.setHasSummaryCards;
  const onAddSummaryCard = summaryCardsContext?.addCardButtonVisible === false ? undefined : onAddSummaryCardProp;

  const gridConfig = useContext(GridConfigContext);
  useNoPullToRefresh();

  useEffect(() => {
    if (title) {
      document.title = `${title} · ${brand.name}`;
    }
  }, [title, brand.name]);

  const [tabsPlacementState, setTabsPlacementState] = useState<TabsPlacement>(() => {
    if (propTabsPlacement) return propTabsPlacement;
    const saved = localStorage.getItem('numtable_tabs_placement');
    if (saved === 'top' || saved === 'bottom' || saved === 'left') return saved;
    if (localStorage.getItem('numtable_sheet_sidebar_open') === 'true') return 'left';
    return DEFAULT_TABS_PLACEMENT;
  });

  useEffect(() => {
    if (propTabsPlacement) {
      setTabsPlacementState(propTabsPlacement);
    }
  }, [propTabsPlacement]);

  useEffect(() => {
    if (gridConfig?.tabsPlacement && (gridConfig.tabsPlacement === 'top' || gridConfig.tabsPlacement === 'bottom' || gridConfig.tabsPlacement === 'left')) {
      setTabsPlacementState(gridConfig.tabsPlacement);
    }
  }, [gridConfig?.tabsPlacement]);

  const desktopPlacement: TabsPlacement = propTabsPlacement ?? (
    gridConfig?.tabsPlacement ?? tabsPlacementState
  );
  // Mobile keeps its own placement; until one is picked it follows desktop.
  const device = useDevice();
  const [mobilePlacementState, setMobilePlacementState] = useState<TabsPlacement | null>(() => {
    const saved = localStorage.getItem('numtable_tabs_placement_mobile');
    return saved === 'top' || saved === 'bottom' || saved === 'left' ? saved : null;
  });
  const mobilePlacement: TabsPlacement = propTabsPlacement ?? (
    (gridConfig ? gridConfig.tabsPlacementMobile : mobilePlacementState) ?? desktopPlacement
  );
  const effectivePlacement: TabsPlacement = device === 'mobile' ? mobilePlacement : desktopPlacement;
  // Which device the tab placement menu sets.
  const [placementScope, setPlacementScope] = useState<DeviceScope>('all');

  const changeTabsPlacement = useCallback((target: Device, nextPlacement: TabsPlacement) => {
    if (target === 'mobile') {
      setMobilePlacementState(nextPlacement);
      localStorage.setItem('numtable_tabs_placement_mobile', nextPlacement);
      if (gridConfig?.canEditShared) {
        void gridConfig.save(GRID_SETTING_KEYS.tabsPlacementMobile, nextPlacement).catch(() => undefined);
      }
      return;
    }
    setTabsPlacementState(nextPlacement);
    localStorage.setItem('numtable_tabs_placement', nextPlacement);
    localStorage.setItem('numtable_sheet_sidebar_open', String(nextPlacement === 'left'));
    onTabsPlacementChange?.(nextPlacement);
    if (gridConfig?.canEditShared) {
      void gridConfig.save(GRID_SETTING_KEYS.tabsPlacement, nextPlacement).catch(() => undefined);
    }
  }, [gridConfig, onTabsPlacementChange]);

  const [editingTab, setEditingTab] = useState<string | null>(null);
  const [editingTabName, setEditingTabName] = useState('');
  const [openTabMenu, setOpenTabMenu] = useState<string | null>(null);
  const tabSubmenuItems = useRef(new Map<string, GridPageTabMenuItem>());
  const [openTabSubmenu, setOpenTabSubmenu] = useState<{
    id: string;
    items: readonly GridPageTabMenuItem[];
    style: { left: number; top: number };
  } | null>(null);
  const submenuCloseTimer = useRef<number | null>(null);
  const [openSheetList, setOpenSheetList] = useState(false);

  const [sheetSidebarWidth, setSheetSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('numtable_sheet_sidebar_width');
    const parsed = saved ? parseInt(saved, 10) : 208; // 208px = w-52
    return Number.isFinite(parsed) && parsed >= 160 && parsed <= 500 ? parsed : 208;
  });
  const sheetResizeDrag = useRef<{ x0: number; w0: number } | null>(null);

  const startSheetSidebarResize = (e: React.PointerEvent) => {
    e.preventDefault();
    sheetResizeDrag.current = { x0: e.clientX, w0: sheetSidebarWidth };
    const onPointerMove = (event: PointerEvent) => {
      if (!sheetResizeDrag.current) return;
      const delta = event.clientX - sheetResizeDrag.current.x0;
      const next = Math.max(160, Math.min(500, sheetResizeDrag.current.w0 + delta));
      setSheetSidebarWidth(next);
      localStorage.setItem('numtable_sheet_sidebar_width', String(next));
    };
    const onPointerUp = () => {
      sheetResizeDrag.current = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  useEffect(() => {
    localStorage.setItem('numtable_sheet_sidebar_width', String(sheetSidebarWidth));
  }, [sheetSidebarWidth]);

  const [cardsSidebarWidth, setCardsSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('numtable_cards_sidebar_width');
    const parsed = saved ? parseInt(saved, 10) : 240; // 240px = w-60
    return Number.isFinite(parsed) && parsed >= 160 && parsed <= 500 ? parsed : 240;
  });
  const cardsResizeDrag = useRef<{ x0: number; w0: number } | null>(null);

  const startCardsSidebarResize = (e: React.PointerEvent) => {
    e.preventDefault();
    cardsResizeDrag.current = { x0: e.clientX, w0: cardsSidebarWidth };
    const onPointerMove = (event: PointerEvent) => {
      if (!cardsResizeDrag.current) return;
      const delta = cardsResizeDrag.current.x0 - event.clientX;
      const next = Math.max(160, Math.min(500, cardsResizeDrag.current.w0 + delta));
      setCardsSidebarWidth(next);
      localStorage.setItem('numtable_cards_sidebar_width', String(next));
    };
    const onPointerUp = () => {
      cardsResizeDrag.current = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  useEffect(() => {
    localStorage.setItem('numtable_cards_sidebar_width', String(cardsSidebarWidth));
  }, [cardsSidebarWidth]);

  const [sheetListMenuPosition, setSheetListMenuPosition] = useState<MenuPosition>({ left: 8, top: 8 });
  const [tabMenuPosition, setTabMenuPosition] = useState<MenuPosition>({ left: 8, top: 8 });
  const [addTabMenuPosition, setAddTabMenuPosition] = useState<MenuPosition>({ left: 8, top: 8 });
  const [openAddTabMenu, setOpenAddTabMenu] = useState(false);
  const [headerRowMode, setHeaderRowMode] = useState<'all' | 'cards-buttons' | 'separate'>('all');
  const headerLayoutRef = useRef<HTMLDivElement>(null);
  const sheetsGroupRef = useRef<HTMLDivElement>(null);
  const cardsGroupRef = useRef<HTMLDivElement>(null);
  const buttonsGroupRef = useRef<HTMLDivElement>(null);
  const sheetsScrollRef = useRef<HTMLDivElement>(null);
  const bottomSheetsScrollRef = useRef<HTMLDivElement>(null);
  const cardsScrollRef = useRef<HTMLDivElement>(null);
  const buttonsScrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [controlsMenuPosition, setControlsMenuPosition] = useState<MenuPosition | null>(null);
  const topBarHosted = useTopBarMenuHosted();
  const topBarSlot = useTopBarMenuSlot();
  const addTabRef = useRef<HTMLButtonElement>(null);
  const sheetListTriggerRef = useRef<HTMLButtonElement>(null);
  const [overflowState, setOverflowState] = useState({
    sheets: { previous: false, next: false },
    cards: { previous: false, next: false },
    buttons: { previous: false, next: false },
  });
  const [draggedTabHref, setDraggedTabHref] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{ href: string; position: 'before' | 'after' } | null>(null);

  const handleTabDragStart = (event: React.DragEvent, tabHref: string) => {
    event.dataTransfer.setData('text/plain', tabHref);
    event.dataTransfer.effectAllowed = 'move';
    setDraggedTabHref(tabHref);
  };

  const handleTabDragOver = (
    event: React.DragEvent,
    targetHref: string,
    orientation: 'horizontal' | 'vertical',
  ) => {
    if (!draggedTabHref || draggedTabHref === targetHref) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const rect = event.currentTarget.getBoundingClientRect();
    let position: 'before' | 'after';
    if (orientation === 'horizontal') {
      const midX = rect.left + rect.width / 2;
      position = event.clientX < midX ? 'before' : 'after';
    } else {
      const midY = rect.top + rect.height / 2;
      position = event.clientY < midY ? 'before' : 'after';
    }
    setDropIndicator((prev) => {
      if (prev?.href === targetHref && prev?.position === position) return prev;
      return { href: targetHref, position };
    });
  };

  const handleTabDragLeave = (event: React.DragEvent, targetHref: string) => {
    const related = event.relatedTarget as Node | null;
    if (related && event.currentTarget.contains(related)) return;
    setDropIndicator((prev) => (prev?.href === targetHref ? null : prev));
  };

  const handleTabDragEnd = () => {
    setDraggedTabHref(null);
    setDropIndicator(null);
  };

  const handleTabDrop = (
    event: React.DragEvent,
    targetHref: string,
    orientation: 'horizontal' | 'vertical',
  ) => {
    event.preventDefault();
    if (!draggedTabHref || !tabs || draggedTabHref === targetHref) {
      handleTabDragEnd();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    let position: 'before' | 'after';
    if (orientation === 'horizontal') {
      const midX = rect.left + rect.width / 2;
      position = event.clientX < midX ? 'before' : 'after';
    } else {
      const midY = rect.top + rect.height / 2;
      position = event.clientY < midY ? 'before' : 'after';
    }

    const sourceIndex = tabs.findIndex((t) => t.href === draggedTabHref);
    const targetIndexInTabs = tabs.findIndex((t) => t.href === targetHref);

    if (sourceIndex !== -1 && targetIndexInTabs !== -1 && sourceIndex !== targetIndexInTabs) {
      let destinationIndex = targetIndexInTabs;
      if (position === 'after') {
        destinationIndex = sourceIndex < targetIndexInTabs ? targetIndexInTabs : targetIndexInTabs + 1;
      } else {
        destinationIndex = sourceIndex < targetIndexInTabs ? targetIndexInTabs - 1 : targetIndexInTabs;
      }
      if (destinationIndex !== sourceIndex) {
        const reordered = [...tabs];
        const [removed] = reordered.splice(sourceIndex, 1);
        if (removed) {
          reordered.splice(destinationIndex, 0, removed);
          if (onTabReorder) {
            void runWithSaveState(() => onTabReorder(reordered, sourceIndex, destinationIndex)).catch(() => undefined);
          }
        }
      }
    }
    handleTabDragEnd();
  };

  const { t } = useI18n();
  const navigate = useNavigate();

  /**
   * Every tab menu offers the trash, so a document's deleted things are reachable
   * from the same place in every module rather than only where a page remembered to
   * wire it up. The default opens the documents trash; a page whose tab has a trash
   * of its own - the investments's positions - passes `onTabOpenTrash` to take over.
   */
  const openTrash = useCallback((tab: GridPageTab) => {
    if (onTabOpenTrash) onTabOpenTrash(tab);
    else navigate('/folders/trash');
  }, [navigate, onTabOpenTrash]);

  const effectiveOverflowLabels: GridPageOverflowLabels = overflowLabels ?? {
    previous: t('sheet.scrollPrevious'),
    next: t('sheet.scrollNext'),
  };

  const effectiveTabMenuLabels: GridPageTabMenuLabels = {
    copyTo: tabMenuLabels?.copyTo ?? t('sheet.tabMenu.copyTo'),
    delete: tabMenuLabels?.delete ?? t('sheet.tabMenu.delete'),
    downloadXlsx: tabMenuLabels?.downloadXlsx ?? t('sheet.tabMenu.downloadXlsx'),
    downloadDocx: tabMenuLabels?.downloadDocx ?? t('sheet.tabMenu.downloadDocx'),
    duplicate: tabMenuLabels?.duplicate ?? t('sheet.tabMenu.duplicate'),
    duplicateWithData: tabMenuLabels?.duplicateWithData ?? t('sheet.tabMenu.duplicateWithData'),
    duplicateHeaders: tabMenuLabels?.duplicateHeaders ?? t('sheet.tabMenu.duplicateHeaders'),
    hide: tabMenuLabels?.hide ?? t('sheet.tabMenu.hide'),
    rename: tabMenuLabels?.rename ?? t('sheet.tabMenu.rename'),
    sheetList: tabMenuLabels?.sheetList ?? t('sheet.tabMenu.label'),
    placement: tabMenuLabels?.placement ?? t('sheet.tabMenu.placement'),
    placementTop: tabMenuLabels?.placementTop ?? t('sheet.tabMenu.placementTop'),
    placementBottom: tabMenuLabels?.placementBottom ?? t('sheet.tabMenu.placementBottom'),
    placementLeft: tabMenuLabels?.placementLeft ?? t('sheet.tabMenu.placementLeft'),
    showErrors: tabMenuLabels?.showErrors ?? t('sheet.tabMenu.showErrors'),
    trash: tabMenuLabels?.trash ?? t('sheet.tabMenu.trash') ?? 'Trash',
  };

  const hasRawCards = cards !== undefined || (summaryCards && summaryCards.length > 0) || Boolean(onAddSummaryCard);
  useEffect(() => {
    setHasSummaryCards?.(Boolean(hasRawCards));
  }, [hasRawCards, setHasSummaryCards]);
  const hasCardItems = cards !== undefined || Boolean(summaryCards && summaryCards.length > 0);
  const setHasCardItems = summaryCardsContext?.setHasCardItems;
  useEffect(() => {
    setHasCardItems?.(hasCardItems);
  }, [hasCardItems, setHasCardItems]);

  const summaryCardsPlacement = summaryCardsContext?.summaryCardsPlacement ?? 'top';

  const effectiveCards = !summaryCardsVisible || summaryCardsPlacement !== 'top'
    ? undefined
    : cards !== undefined
      ? cards
      : (summaryCards && summaryCards.length > 0) || onAddSummaryCard
        ? <GridSummaryCards cards={summaryCards ?? []} onAddCard={onAddSummaryCard} addCardLabel={addSummaryCardLabel ?? t('summaryCards.addCard')} />
        : undefined;

  const hasTabs = Boolean(tabs && tabs.length > 0);
  const canDeleteTab = (tabs?.length ?? 0) > 1;
  const hasTabsInTopBar = hasTabs && effectivePlacement === 'top';
  const hasTabsAtBottom = hasTabs && effectivePlacement === 'bottom';
  const hasTabsInSidebar = hasTabs && effectivePlacement === 'left';
  const hasSplitHeader = hasTabsInTopBar || effectiveCards !== undefined || buttons !== undefined;
  const hasHeader = Boolean(header || effectiveCards || buttons || title || hasTabsInTopBar);
  const hasTabMenu = true; // Always allow tab options menu including tab placement

  useEffect(() => {
    if (!openTabMenu && !openSheetList && !openAddTabMenu) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[role="menu"]') || target?.closest('[aria-haspopup="menu"]')) {
        return;
      }
      setOpenTabMenu(null);
      setOpenSheetList(false);
      setOpenAddTabMenu(false);
      setOpenTabSubmenu(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenTabMenu(null);
        setOpenSheetList(false);
        setOpenAddTabMenu(false);
        setOpenTabSubmenu(null);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openTabMenu, openSheetList, openAddTabMenu]);

  const editor = useOptionalEditor();
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markSaved = useCallback(() => {
    editorRef.current?.setSaveState('saved');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      editorRef.current?.setSaveState('clean');
    }, 2500);
  }, []);

  const markSaving = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    editorRef.current?.setSaveState('saving');
  }, []);

  const markFailed = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    editorRef.current?.setSaveState('failed');
  }, []);

  const runWithSaveState = useCallback(async (action: () => unknown | Promise<unknown>) => {
    markSaving();
    try {
      await action();
      markSaved();
    } catch (err) {
      markFailed();
      throw err;
    }
  }, [markSaving, markSaved, markFailed]);

  const finishTabRename = (tab: GridPageTab) => {
    const name = editingTabName.trim();
    setEditingTab(null);
    if (name && name !== tab.labelKey && onTabRename) {
      void runWithSaveState(() => onTabRename(tab, name)).catch(() => undefined);
    }
  };

  const visibleTabs = tabs?.filter((tab) => !tab.hidden) ?? [];
  /**
   * Places a tab menu beside its trigger. Opening upward it hangs from the trigger's top
   * edge (anchored by `bottom`), so however tall it really is it never runs off-screen;
   * either way the height is capped to the room available and the rest scrolls.
   */
  const menuPosition = (rect: DOMRect, width: number, estimatedHeight = 360): MenuPosition => {
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpwards = effectivePlacement === 'bottom' || spaceBelow < estimatedHeight + 16;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    return openUpwards
      ? { left, bottom: window.innerHeight - rect.top + 4, maxHeight: Math.max(120, rect.top - 12) }
      : { left, top: rect.bottom + 4, maxHeight: Math.max(120, spaceBelow - 12) };
  };

  const updateOverflowState = useCallback((key: 'sheets' | 'cards' | 'buttons', element: HTMLDivElement | null) => {
    if (!element) return;
    const nextState = {
      previous: element.scrollLeft > 1,
      next: element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
    };
    setOverflowState((currentState) => {
      const current = currentState[key];
      if (current.previous === nextState.previous && current.next === nextState.next) return currentState;
      return { ...currentState, [key]: nextState };
    });
  }, []);

  const updateAllOverflowState = useCallback(() => {
    const sheetsElem = effectivePlacement === 'bottom' ? bottomSheetsScrollRef.current : sheetsScrollRef.current;
    updateOverflowState('sheets', sheetsElem);
    updateOverflowState('cards', cardsScrollRef.current);
    updateOverflowState('buttons', buttonsScrollRef.current);
  }, [effectivePlacement, updateOverflowState]);

  const measureHeaderRows = useCallback(() => {
    const container = headerLayoutRef.current;
    if (!container) return;
    const sheetsScroll = sheetsScrollRef.current;
    const cardsScroll = cardsScrollRef.current;
    const buttonsScroll = buttonsScrollRef.current;
    const availableWidth = container.clientWidth;
    const gap = 8;
    const intrinsicWidth = (scrollContainer: HTMLElement) => {
      const content = scrollContainer.firstElementChild instanceof HTMLElement ? scrollContainer.firstElementChild : scrollContainer;
      const measurableRow = content.firstElementChild instanceof HTMLElement && content.firstElementChild.tagName === 'NAV'
        ? content.firstElementChild
        : content;
      const rowStyle = window.getComputedStyle(measurableRow);
      const rowGap = Number.parseFloat(rowStyle.columnGap) || 0;
      const children = [...measurableRow.children].filter((child): child is HTMLElement => {
        return child instanceof HTMLElement && window.getComputedStyle(child).position !== 'absolute';
      });
      return children.reduce((width, child, index) => {
        const style = window.getComputedStyle(child);
        const margin = (Number.parseFloat(style.marginLeft) || 0) + (Number.parseFloat(style.marginRight) || 0);
        return width + child.getBoundingClientRect().width + margin + (index > 0 ? rowGap : 0);
      }, 0);
    };
    const sheetTriggerWidth = sheetListTriggerRef.current?.getBoundingClientRect().width ?? 0;
    const addTabWidth = addTabRef.current?.getBoundingClientRect().width ?? 0;
    const sheetsWidth = (hasTabsInTopBar && sheetsScroll) ? intrinsicWidth(sheetsScroll) + (sheetTriggerWidth ? sheetTriggerWidth + gap : 0) + (addTabWidth ? addTabWidth + gap : 0) : 0;
    const cardsWidth = cardsScroll ? intrinsicWidth(cardsScroll) : 0;
    const buttonsWidth = buttonsScroll ? intrinsicWidth(buttonsScroll) : 0;
    const activeGroupsCount = (hasTabsInTopBar && sheetsScroll ? 1 : 0) + (cardsScroll ? 1 : 0) + (buttonsScroll ? 1 : 0);
    const totalGaps = Math.max(0, activeGroupsCount - 1) * gap;
    // Cards placed right of the sheet names stay on that row; the sheet names scroll instead.
    const nextMode = (hasTabsInTopBar && sheetsScroll && cardsScroll) || sheetsWidth + cardsWidth + buttonsWidth + totalGaps <= availableWidth
      ? 'all'
      : cardsWidth + buttonsWidth + gap <= availableWidth
        ? 'cards-buttons'
        : 'separate';
    setHeaderRowMode((currentMode) => currentMode === nextMode ? currentMode : nextMode);
  }, [hasTabsInTopBar]);

  useLayoutEffect(() => {
    measureHeaderRows();
    updateAllOverflowState();
    const observer = new ResizeObserver(() => {
      measureHeaderRows();
      updateAllOverflowState();
    });
    const elements = [
      headerLayoutRef.current,
      sheetsGroupRef.current,
      cardsGroupRef.current,
      buttonsGroupRef.current,
      sheetsScrollRef.current,
      bottomSheetsScrollRef.current,
      cardsScrollRef.current,
      buttonsScrollRef.current,
    ];
    elements.forEach((element) => { if (element) observer.observe(element); });
    window.addEventListener('resize', measureHeaderRows);
    window.addEventListener('resize', updateAllOverflowState);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureHeaderRows);
      window.removeEventListener('resize', updateAllOverflowState);
    };
  }, [hasSplitHeader, measureHeaderRows, tabs?.length, updateAllOverflowState, effectivePlacement]);

  const scrollHeaderGroup = (element: HTMLDivElement | null, direction: 'previous' | 'next') => {
    if (!element) return;
    const distance = Math.max(element.clientWidth * 0.7, 120) * (direction === 'previous' ? -1 : 1);
    element.scrollBy({ left: distance, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  const selectTab = useCallback((tab: GridPageTab) => {
    if (tab.hidden) {
      void Promise.resolve(runWithSaveState(() => onTabShow?.(tab)))
        .then(() => onTabChange?.(tab))
        .catch(() => undefined);
      return;
    }
    onTabChange?.(tab);
  }, [onTabChange, onTabShow, runWithSaveState]);

  useEffect(() => {
    if (!tabs || tabs.length <= 1) return;
    const handleTabShortcut = (event: KeyboardEvent) => {
      if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
      // When Alt+Arrow is pressed, navigate sheets
      const currentIndex = tabs.findIndex((t) => t.href === activeTab);
      if (currentIndex === -1) return;

      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'ArrowUp') {
        const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        const target = tabs[prevIndex];
        if (target) selectTab(target);
      } else if (event.key === 'ArrowDown') {
        const nextIndex = (currentIndex + 1) % tabs.length;
        const target = tabs[nextIndex];
        if (target) selectTab(target);
      }
    };

    window.addEventListener('keydown', handleTabShortcut, true);
    return () => window.removeEventListener('keydown', handleTabShortcut, true);
  }, [activeTab, selectTab, tabs]);

  const closeTabSubmenu = () => {
    if (submenuCloseTimer.current !== null) window.clearTimeout(submenuCloseTimer.current);
    submenuCloseTimer.current = window.setTimeout(() => setOpenTabSubmenu(null), 120);
  };

  const keepTabSubmenu = () => {
    if (submenuCloseTimer.current !== null) window.clearTimeout(submenuCloseTimer.current);
  };

  const renderTabMenuItem = (item: GridPageTabMenuItem) => {
    const hasSubmenu = Boolean(item.items && item.items.length > 0);
    if (hasSubmenu) tabSubmenuItems.current.set(item.id, item);
    return (
      <button
        key={item.id}
        role={item.selected === undefined ? 'menuitem' : 'menuitemradio'}
        {...(item.selected === undefined ? {} : { 'aria-checked': item.selected })}
        {...(hasSubmenu ? { 'aria-haspopup': 'menu' as const, 'aria-expanded': openTabSubmenu?.id === item.id } : {})}
        type="button"
        onMouseEnter={hasSubmenu ? (event) => {
          keepTabSubmenu();
          const rect = event.currentTarget.getBoundingClientRect();
          const height = (item.items?.length ?? 0) * 34 + 8 + (item.itemsHeader ? 40 : 0);
          setOpenTabSubmenu({
            id: item.id,
            items: item.items ?? [],
            style: {
              left: Math.max(8, Math.min(rect.right + 2, window.innerWidth - 208 - 8)),
              top: Math.max(8, Math.min(rect.top - 4, window.innerHeight - height - 8)),
            },
          });
        } : keepTabSubmenu}
        onMouseLeave={hasSubmenu ? closeTabSubmenu : undefined}
        onClick={(event) => {
          if (hasSubmenu) {
            event.preventDefault();
            keepTabSubmenu();
            return;
          }
          setOpenTabMenu(null);
          setOpenSheetList(false);
          setOpenTabSubmenu(null);
          item.onSelect?.();
        }}
        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs sm:text-sm hover:bg-surface-3 ${
          item.selected ? 'font-semibold text-brand' : 'text-fg'
        }`}
      >
        {item.icon}
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.selected && (
          <svg aria-hidden="true" className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        )}
        {hasSubmenu && (
          <svg aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        )}
      </button>
    );
  };

  const renderErrorBadge = (tab: GridPageTab) => {
    const count = tab.errorCount ?? 0;
    if (count <= 0) return null;
    const label = tabMenuLabels?.errorCount?.replace('{count}', String(count)) ?? t('sheet.tabMenu.errorCount', { count });
    const content = count > 99 ? '99+' : String(count);
    const className = 'ml-1 inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white';
    if (!onTabShowErrors) return <span role="img" aria-label={label} title={label} className={className}>{content}</span>;
    // A span, not a button: in the sidebar and sheet list it sits inside the tab's own button.
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label={label}
        title={label}
        data-testid="tab-error-badge"
        onClick={(event) => { event.stopPropagation(); onTabShowErrors(tab); }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          event.stopPropagation();
          onTabShowErrors(tab);
        }}
        className={`${className} cursor-pointer hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus`}
      >
        {content}
      </span>
    );
  };

  const renderShowErrorsItem = (tab: GridPageTab) => (
    onTabShowErrors && !tab.isTextTab && !tab.isPivotTab && effectiveTabMenuLabels.showErrors
      ? <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); onTabShowErrors(tab); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="alertCircle" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.showErrors}</button>
      : null
  );

  const renderTabMenuItems = () => {
    if (!tabMenuItems || tabMenuItems.length === 0) return null;
    return (
      <>
        {tabMenuItems.map(renderTabMenuItem)}
        <div className="my-1 border-t border-border" />
      </>
    );
  };

  /** One flyout for whichever row is hovered; menus are portalled, so is this. */
  // The flyout reads its row as rendered now, not as it was when hovered, so a
  // switch inside it updates the entries below it at once.
  const liveSubmenu = openTabSubmenu ? tabSubmenuItems.current.get(openTabSubmenu.id) : undefined;
  const tabSubmenu = openTabSubmenu && (openTabMenu || openSheetList) ? createPortal(
    <div
      role="menu"
      style={openTabSubmenu.style}
      onMouseEnter={keepTabSubmenu}
      onMouseLeave={closeTabSubmenu}
      className="fixed z-[101] flex max-h-[calc(100vh-24px)] w-52 flex-col overflow-y-auto rounded-xl border border-border bg-surface py-1 text-xs sm:text-sm text-fg shadow-xl"
    >
      {liveSubmenu?.itemsHeader && (
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 pb-1.5 pt-1">
          {liveSubmenu.itemsHeader}
        </div>
      )}
      {(liveSubmenu?.items ?? openTabSubmenu.items).map(renderTabMenuItem)}
    </div>,
    document.body,
  ) : null;

  /** Both duplicate flavors live behind one row, hovered open like the others. */
  const renderDuplicateMenuItem = (tab: GridPageTab) => {
    if (!onTabDuplicate && !onTabDuplicateHeaders) return null;
    return renderTabMenuItem({
      id: 'duplicate',
      icon: <Icon name="copy" className="h-4 w-4 shrink-0 text-text-muted" />,
      label: effectiveTabMenuLabels.duplicate,
      items: [
        ...(onTabDuplicate ? [{
          id: 'duplicate-with-data',
          label: effectiveTabMenuLabels.duplicateWithData ?? effectiveTabMenuLabels.duplicate,
          onSelect: () => { void runWithSaveState(() => onTabDuplicate(tab)).catch(() => undefined); },
        }] : []),
        ...(onTabDuplicateHeaders ? [{
          id: 'duplicate-headers',
          label: effectiveTabMenuLabels.duplicateHeaders ?? '',
          onSelect: () => { void runWithSaveState(() => onTabDuplicateHeaders(tab)).catch(() => undefined); },
        }] : []),
      ],
    });
  };

  const renderColumnsMenuItem = (tab: GridPageTab) => {
    const item = tabColumnsMenuItem?.(tab);
    return item ? renderTabMenuItem(item) : null;
  };

  const renderTabPlacementOptions = () => (
    <>
      <div className="my-1 border-t border-border" />
      {renderTabMenuItem({
        id: 'tabs-placement',
        icon: <Icon name="layout" className="h-4 w-4 shrink-0 text-text-muted" />,
        label: effectiveTabMenuLabels.placement ?? '',
        itemsHeader: (
          <>
            <span className="min-w-0 truncate text-xs text-text-muted">{t('device.setFor')}</span>
            <DeviceSwitch value={placementScope} onChange={setPlacementScope} label={t('device.setFor')} />
          </>
        ),
        items: ([
          ['top', effectiveTabMenuLabels.placementTop],
          ['bottom', effectiveTabMenuLabels.placementBottom],
          ['left', effectiveTabMenuLabels.placementLeft],
        ] as const).map(([placement, label]) => ({
          id: `tabs-placement-${placement}`,
          label: label ?? placement,
          selected: placementScope === 'all'
            ? desktopPlacement === placement && mobilePlacement === placement
            : (placementScope === 'mobile' ? mobilePlacement : desktopPlacement) === placement,
          onSelect: () => {
            if (placementScope !== 'mobile') changeTabsPlacement('desktop', placement);
            if (placementScope !== 'desktop') changeTabsPlacement('mobile', placement);
          },
        })),
      })}
    </>
  );

  const sheetListTrigger = hasTabs ? (
    <>
      <button
        ref={sheetListTriggerRef}
        type="button"
        aria-label={effectiveTabMenuLabels.sheetList}
        aria-haspopup="menu"
        aria-expanded={openSheetList}
        onClick={(event) => {
          setSheetListMenuPosition(menuPosition(event.currentTarget.getBoundingClientRect(), 240));
          setOpenSheetList((open) => !open);
        }}
        className="nt-icon-trigger mr-1 shrink-0 text-text-muted transition-colors hover:text-text"
      >
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      {openSheetList && createPortal(
        <div role="menu" style={sheetListMenuPosition} className="fixed z-[100] flex w-60 flex-col overflow-y-auto rounded-xl border border-border bg-surface py-1 text-xs sm:text-sm text-fg shadow-xl">
          <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            {effectiveTabMenuLabels.sheetList}
          </div>
          {tabs!.map((tab) => (
            <button
              role="menuitem"
              key={tab.href}
              type="button"
              onClick={() => {
                setOpenSheetList(false);
                selectTab(tab);
              }}
              className={`flex min-w-0 items-center gap-2 px-3 py-2 text-left text-sheet-name-text hover:bg-surface-3 ${
                tab.href === activeTab ? 'bg-surface-2 font-bold text-brand' : ''
              }`}
            >
              <span className="min-w-0 flex-1 truncate">{tab.labelKey}</span>
                {renderErrorBadge(tab)}
              {tab.hidden && <svg aria-label="" aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" /><circle cx="12" cy="12" r="2.5" /></svg>}
            </button>
          ))}
          {renderTabPlacementOptions()}
        </div>,
        document.body,
      )}
    </>
  ) : null;

  const hasAddMenu = Boolean(onAddTab && onAddTextTab);
  const addTab = onAddTab && addTabLabel ? (
    <>
      <button
        type="button"
        ref={addTabRef}
        onClick={(event) => {
          if (hasAddMenu) {
            setAddTabMenuPosition(menuPosition(event.currentTarget.getBoundingClientRect(), 220, 100));
            setOpenAddTabMenu((open) => !open);
          } else {
            void runWithSaveState(() => onAddTab()).catch(() => undefined);
          }
        }}
        disabled={addTabDisabled}
        aria-label={addTabLabel}
        aria-haspopup={hasAddMenu ? 'menu' : undefined}
        aria-expanded={hasAddMenu ? openAddTabMenu : undefined}
        className="nt-control ml-1 shrink-0 px-2 text-fg disabled:opacity-50"
      >
        <span aria-hidden="true">+</span>
      </button>
      {hasAddMenu && openAddTabMenu && createPortal(
        <div
          role="menu"
          style={addTabMenuPosition}
          className="fixed z-[100] flex w-64 max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border border-border bg-surface py-1 text-xs sm:text-sm text-fg shadow-xl"
        >
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setOpenAddTabMenu(false);
              void runWithSaveState(() => onAddTab()).catch(() => undefined);
            }}
            className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3 transition-colors"
          >
            <svg className="w-4 h-4 text-brand shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/><path d="M15 3v18"/>
            </svg>
            <span>{addTabLabel || t('sheet.addTableSheet')}</span>
          </button>
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setOpenAddTabMenu(false);
              void runWithSaveState(() => onAddTextTab?.()).catch(() => undefined);
            }}
            className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3 transition-colors"
          >
            <svg className="w-4 h-4 text-accent shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z"/><path d="M8 7h8"/><path d="M8 11h8"/><path d="M8 15h5"/>
            </svg>
            <span>{addTextTabLabel || t('sheet.addTextSheet')}</span>
          </button>
          {onAddPivotTab && (
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                setOpenAddTabMenu(false);
                void runWithSaveState(() => onAddPivotTab()).catch(() => undefined);
              }}
              className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3 transition-colors"
            >
              <svg className="w-4 h-4 text-brand shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M9 3v18"/><path d="M13 13h4"/><path d="M15 11v4"/>
              </svg>
              <span>{addPivotTabLabel || t('sheet.addPivotSheet')}</span>
            </button>
          )}
          {onAddReconciliationTab && (
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                setOpenAddTabMenu(false);
                void runWithSaveState(() => onAddReconciliationTab()).catch(() => undefined);
              }}
              className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3 transition-colors"
            >
              <svg className="w-4 h-4 text-accent shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 4v16"/><path d="M3 8l4-4 4 4"/><path d="M17 20V4"/><path d="M13 16l4 4 4-4"/>
              </svg>
              <span>{addReconciliationTabLabel || t('sheet.addReconciliationSheet')}</span>
            </button>
          )}
          {onAddLinkedTab && (
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                setOpenAddTabMenu(false);
                onAddLinkedTab();
              }}
              className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3 transition-colors"
            >
              <svg className="w-4 h-4 text-brand shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="8" height="18" x="2" y="3" rx="1.5"/><rect width="8" height="18" x="14" y="3" rx="1.5"/><path d="M2 8h8"/><path d="M14 8h8"/>
              </svg>
              <span className="min-w-0 truncate">{addLinkedTabLabel || t('sheet.addLinkedSheet')}</span>
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  ) : null;

  const renderHorizontalTabs = () => {
    if (!hasTabs) return null;
    return (
      <nav aria-label={effectiveTabMenuLabels.sheetList} className="relative flex min-w-0 max-w-full flex-nowrap items-center">
        <div className="flex min-w-0 flex-nowrap items-center">
          {visibleTabs.map((tab) => {
            const isDraggable = !editingTab && Boolean(onTabReorder) && visibleTabs.length > 1;
            const isDragging = draggedTabHref === tab.href;
            const isDropTarget = dropIndicator?.href === tab.href;
            const isBefore = isDropTarget && dropIndicator?.position === 'before';
            const isAfter = isDropTarget && dropIndicator?.position === 'after';

            return editingTab === tab.href ? (
              <input
                key={tab.href}
                autoFocus
                value={editingTabName}
                onChange={(event) => setEditingTabName(event.target.value)}
                onBlur={() => finishTabRename(tab)}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                  if (event.key === 'Escape') setEditingTab(null);
                }}
                aria-label={tab.labelKey}
                className="mx-1 my-1 h-7 min-w-20 max-w-40 rounded-full border border-focus bg-bg px-3 text-sm font-bold text-fg outline-none focus:ring-2 focus:ring-focus"
              />
            ) : (
              <div
                key={tab.href}
                draggable={isDraggable}
                onDragStart={(e) => handleTabDragStart(e, tab.href)}
                onDragOver={(e) => handleTabDragOver(e, tab.href, 'horizontal')}
                onDragLeave={(e) => handleTabDragLeave(e, tab.href)}
                onDrop={(e) => handleTabDrop(e, tab.href, 'horizontal')}
                onDragEnd={handleTabDragEnd}
                className={`group relative flex shrink-0 items-center border-b-2 transition-opacity ${
                  isDragging ? 'opacity-40' : ''
                } ${
                  tab.href === activeTab ? 'border-brand text-brand font-bold' : 'border-transparent text-text-muted hover:text-text'
                } ${hasTabMenu && tab.href === activeTab ? 'pr-4' : ''} ${
                  isDraggable ? 'cursor-grab active:cursor-grabbing' : ''
                }`}
              >
                {isBefore && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute left-0 top-1 bottom-1 w-0.5 rounded-full bg-brand shadow-sm z-20 -translate-x-1/2"
                  />
                )}
                {isAfter && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute right-0 top-1 bottom-1 w-0.5 rounded-full bg-brand shadow-sm z-20 translate-x-1/2"
                  />
                )}
                <button
                  type="button"
                  onClick={() => onTabChange?.(tab)}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setTabMenuPosition(menuPosition(event.currentTarget.getBoundingClientRect(), 208));
                    setOpenTabMenu(tab.href);
                  }}
                  {...doubleTapProps(onTabRename ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setEditingTab(tab.href);
                    setEditingTabName(tab.labelKey);
                  } : undefined)}
                  aria-current={tab.href === activeTab ? 'page' : undefined}
                  className={`nt-sheet-tab whitespace-nowrap px-2 py-0.5 text-sm font-bold text-sheet-name-text border-b-2 ${
                    tab.href === activeTab ? 'border-transparent' : 'border-transparent'
                  }`}
                >
                  {tab.labelKey}
                </button>
                {renderErrorBadge(tab)}
                {hasTabMenu && tab.href === activeTab && (
                  <button
                    type="button"
                    aria-label={effectiveTabMenuLabels.rename}
                    aria-haspopup="menu"
                    aria-expanded={openTabMenu === tab.href}
                    onClick={(event) => {
                      event.stopPropagation();
                      setTabMenuPosition(menuPosition(event.currentTarget.getBoundingClientRect(), 208));
                      setOpenTabMenu(openTabMenu === tab.href ? null : tab.href);
                    }}
                    className="absolute right-0 top-1/2 flex h-5 w-4 -translate-y-1/2 items-center justify-center rounded-full opacity-100 transition-colors focus-visible:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus hover:bg-surface-3"
                  >
                    <svg aria-hidden="true" className="h-2.5 w-2.5 fill-current" viewBox="0 0 10 6"><path d="M0 0h10L5 6z" /></svg>
                  </button>
                )}
                {hasTabMenu && openTabMenu === tab.href && createPortal(
                  <div role="menu" style={tabMenuPosition} className="fixed z-[100] flex max-h-[calc(100vh-24px)] w-52 flex-col overflow-y-auto rounded-xl border border-border bg-surface py-1 text-xs sm:text-sm text-fg shadow-xl">
                    {renderTabMenuItems()}
                    {onTabDelete && <button role="menuitem" type="button" disabled={!canDeleteTab} onClick={() => { if (!canDeleteTab) return; setOpenTabMenu(null); void runWithSaveState(() => onTabDelete(tab)).catch(() => undefined); }} className={`flex items-center gap-2 px-3 py-2 text-left ${canDeleteTab ? 'text-danger hover:bg-danger-soft' : 'text-text-muted opacity-40 cursor-not-allowed'}`}><Icon name="trash" className="h-4 w-4 shrink-0" />{effectiveTabMenuLabels.delete}</button>}
                    {renderDuplicateMenuItem(tab)}
                    {renderColumnsMenuItem(tab)}
                    {onTabCopyTo && <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); onTabCopyTo(tab); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="fileExport" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.copyTo}</button>}
                    {onTabRename && <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); setEditingTab(tab.href); setEditingTabName(tab.labelKey); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="pencil" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.rename}</button>}
                    {onTabHide && <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); void runWithSaveState(() => onTabHide(tab)).catch(() => undefined); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="eyeOff" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.hide}</button>}
                    {renderShowErrorsItem(tab)}
                    <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); openTrash(tab); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="trash" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.trash}</button>
                    {tab.isTextTab ? (
                      onTabDownloadDocx && <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); void onTabDownloadDocx(tab); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="install" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.downloadDocx || 'Download .docx'}</button>
                    ) : null}
                    {renderTabPlacementOptions()}
                  </div>,
                  document.body,
                )}
              </div>
            );
          })}
          {addTab}
        </div>
      </nav>
    );
  };

  const renderVerticalTabs = () => {
    if (!hasTabs) return null;
    return (
      <div className="flex flex-col gap-0.5">
        {tabs!.map((tab) => {
          const active = tab.href === activeTab;
          const isDraggable = !editingTab && Boolean(onTabReorder) && (tabs?.length ?? 0) > 1;
          const isDragging = draggedTabHref === tab.href;
          const isDropTarget = dropIndicator?.href === tab.href;
          const isBefore = isDropTarget && dropIndicator?.position === 'before';
          const isAfter = isDropTarget && dropIndicator?.position === 'after';

          return editingTab === tab.href ? (
            <input
              key={tab.href}
              autoFocus
              value={editingTabName}
              onChange={(event) => setEditingTabName(event.target.value)}
              onBlur={() => finishTabRename(tab)}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
                if (event.key === 'Escape') setEditingTab(null);
              }}
              aria-label={tab.labelKey}
              className="my-1 h-9 w-full rounded-xl border border-focus bg-bg px-3 text-sm font-bold text-fg outline-none focus:ring-2 focus:ring-focus"
            />
          ) : (
            <div
              key={tab.href}
              draggable={isDraggable}
              onDragStart={(e) => handleTabDragStart(e, tab.href)}
              onDragOver={(e) => handleTabDragOver(e, tab.href, 'vertical')}
              onDragLeave={(e) => handleTabDragLeave(e, tab.href)}
              onDrop={(e) => handleTabDrop(e, tab.href, 'vertical')}
              onDragEnd={handleTabDragEnd}
              className={`group relative flex min-w-0 items-center transition-opacity ${
                isDragging ? 'opacity-40' : ''
              } ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
            >
              {isBefore && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute left-2 right-2 top-0 h-0.5 rounded-full bg-brand shadow-sm z-20 -translate-y-1/2"
                />
              )}
              {isAfter && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute left-2 right-2 bottom-0 h-0.5 rounded-full bg-brand shadow-sm z-20 translate-y-1/2"
                />
              )}
              <button
                type="button"
                onClick={() => selectTab(tab)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setTabMenuPosition(menuPosition(event.currentTarget.getBoundingClientRect(), 208));
                  setOpenTabMenu(`sidebar-${tab.href}`);
                }}
                {...doubleTapProps(onTabRename ? (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setEditingTab(tab.href);
                  setEditingTabName(tab.labelKey);
                } : undefined)}
                aria-current={active ? 'page' : undefined}
                className={`nt-sheet-tab flex min-h-10 min-w-0 flex-1 items-center gap-2 rounded-xl px-3 text-left text-xs sm:text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${hasTabMenu && active ? 'pr-8' : ''} ${
                  active ? 'bg-surface-3 font-bold text-sheet-name-text' : 'text-text-muted hover:bg-surface-3 hover:text-sheet-name-text'
                }`}
              >
                <span className="min-w-0 flex-1 truncate">{tab.labelKey}</span>
                {renderErrorBadge(tab)}
                {tab.hidden && <svg aria-label="" aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" /><circle cx="12" cy="12" r="2.5" /></svg>}
              </button>
              {hasTabMenu && active && (
                <button
                  type="button"
                  aria-label={effectiveTabMenuLabels.rename}
                  aria-haspopup="menu"
                  aria-expanded={openTabMenu === `sidebar-${tab.href}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setTabMenuPosition(menuPosition(event.currentTarget.getBoundingClientRect(), 208));
                    setOpenTabMenu(openTabMenu === `sidebar-${tab.href}` ? null : `sidebar-${tab.href}`);
                  }}
                  className="absolute right-2 top-1/2 flex h-6 w-5 -translate-y-1/2 items-center justify-center rounded-full opacity-100 transition-colors focus-visible:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus hover:bg-surface-3"
                >
                  <svg aria-hidden="true" className="h-2.5 w-2.5 fill-current" viewBox="0 0 10 6"><path d="M0 0h10L5 6z" /></svg>
                </button>
              )}
              {hasTabMenu && openTabMenu === `sidebar-${tab.href}` && createPortal(
                <div role="menu" style={tabMenuPosition} className="fixed z-[100] flex max-h-[calc(100vh-24px)] w-52 flex-col overflow-y-auto rounded-xl border border-border bg-surface py-1 text-xs sm:text-sm text-fg shadow-xl">
                  {renderTabMenuItems()}
                    {onTabDelete && <button role="menuitem" type="button" disabled={!canDeleteTab} onClick={() => { if (!canDeleteTab) return; setOpenTabMenu(null); void runWithSaveState(() => onTabDelete(tab)).catch(() => undefined); }} className={`flex items-center gap-2 px-3 py-2 text-left ${canDeleteTab ? 'text-danger hover:bg-danger-soft' : 'text-text-muted opacity-40 cursor-not-allowed'}`}><Icon name="trash" className="h-4 w-4 shrink-0" />{effectiveTabMenuLabels.delete}</button>}
                  {renderDuplicateMenuItem(tab)}
                  {renderColumnsMenuItem(tab)}
                  {onTabCopyTo && <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); onTabCopyTo(tab); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="fileExport" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.copyTo}</button>}
                  {onTabRename && <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); setEditingTab(tab.href); setEditingTabName(tab.labelKey); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="pencil" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.rename}</button>}
                  {onTabHide && <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); void runWithSaveState(() => onTabHide(tab)).catch(() => undefined); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="eyeOff" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.hide}</button>}
                  {renderShowErrorsItem(tab)}
                    <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); openTrash(tab); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="trash" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.trash}</button>
                  {tab.isTextTab ? (
                    onTabDownloadDocx && <button role="menuitem" type="button" onClick={() => { setOpenTabMenu(null); void onTabDownloadDocx(tab); }} className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3"><Icon name="install" className="h-4 w-4 shrink-0 text-text-muted" />{effectiveTabMenuLabels.downloadDocx || 'Download .docx'}</button>
                  ) : null}
                  {renderTabPlacementOptions()}
                </div>,
                document.body,
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Phones: the page's special buttons (currency pair, refresh…) live in the top bar's ⋮ menu.
  const hoistButtons = isMobile && topBarHosted;
  const hasControls = buttons !== undefined && buttons !== null;
  const controlsShown = hasControls && (summaryCardsContext?.controlsOnHere ?? true);
  const controlsPlacement = summaryCardsContext?.controlsPlacement ?? 'top';
  // A caret beside the controls picks the devices and the place they show in; with them hidden here it stays at the top.
  const controlsCaret = hasControls && summaryCardsContext && !hoistButtons ? (
    <button
      type="button"
      aria-label={t('grid.controlsOptions')}
      title={t('grid.controlsOptions')}
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const width = 240;
        const spaceBelow = window.innerHeight - rect.bottom;
        const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
        setControlsMenuPosition(spaceBelow < 260
          ? { left, bottom: window.innerHeight - rect.top + 4, maxHeight: Math.max(120, rect.top - 12) }
          : { left, top: rect.bottom + 4, maxHeight: Math.max(120, spaceBelow - 12) });
      }}
      className={`flex h-7 w-5 shrink-0 items-center justify-center rounded text-text-muted transition-colors hover:bg-surface-hover ${controlsMenuPosition ? 'bg-brand/10' : ''}`}
    >
      <Caret />
    </button>
  ) : null;
  const controlsWithCaret = <>{buttons}{controlsCaret}</>;
  const tabButtons = hoistButtons || !hasControls
    ? null
    : controlsShown && controlsPlacement === 'top' ? controlsWithCaret
      : controlsShown ? null : controlsCaret;
  const controlsPlacementOptions: ReadonlyArray<readonly [typeof controlsPlacement, string]> = [
    ['top', t('grid.summaryCardsTop')],
    ['topBelow', t('grid.summaryCardsTopBelow')],
    ['bottom', t('grid.summaryCardsBottom')],
    ['sidebar', t('grid.summaryCardsSidebar')],
  ];
  const controlsMenu = controlsMenuPosition && summaryCardsContext ? createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={() => setControlsMenuPosition(null)} />
      <div
        style={{ left: controlsMenuPosition.left, top: controlsMenuPosition.top, bottom: controlsMenuPosition.bottom, maxHeight: controlsMenuPosition.maxHeight }}
        className="fixed z-50 flex w-60 flex-col overflow-y-auto rounded-lg border border-border bg-surface py-1 text-sm shadow-lg sm:text-xs"
      >
        <div className="flex items-center justify-between gap-3 px-3 pb-1.5 pt-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted">{t('device.showOn')}</span>
          <DeviceSwitch value={summaryCardsContext.controlsDevice} onChange={summaryCardsContext.setControlsDevice} label={t('device.showControlsOn')} />
        </div>
        <div className="px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-text-muted">{t('grid.controlsPlacement')}</div>
        {controlsPlacementOptions.map(([placement, label]) => {
          const active = controlsPlacement === placement && controlsShown;
          return (
            <button
              key={placement}
              type="button"
              onClick={() => {
                summaryCardsContext.setControlsPlacement(placement);
                setControlsMenuPosition(null);
              }}
              className={`flex items-center justify-between px-3 py-1.5 text-left transition-colors hover:bg-surface-hover ${active ? 'font-medium text-brand' : ''}`}
            >
              <span>{label}</span>
              {active && <Icon name="check" className="h-3.5 w-3.5" />}
            </button>
          );
        })}
      </div>
    </>,
    document.body,
  ) : null;
  const hoistedButtons = hoistButtons && topBarSlot && controlsShown
    ? createPortal(<div className="flex flex-wrap items-center gap-2 border-b border-border px-3 pb-2 pt-1 text-fg">{buttons}</div>, topBarSlot)
    : null;

  const sheetSidebar = hasTabs && hasTabsInSidebar ? (
    <aside
      style={{ width: `${sheetSidebarWidth}px` }}
      data-testid="left-tab-sidebar"
      data-hide-while-typing
      className="relative shrink-0 self-stretch select-none border-r border-border bg-surface-2 flex flex-col min-h-0"
    >
      <div className="flex items-center justify-between p-2 border-b border-border">
        <div className="flex items-center gap-1 min-w-0">
          {sheetListTrigger}
          <span className="text-xs font-semibold text-text-muted truncate">
            {effectiveTabMenuLabels.sheetList}
          </span>
        </div>
        {onAddTab && addTabLabel && (
          <button
            type="button"
            onClick={(event) => {
              if (hasAddMenu) {
                setAddTabMenuPosition(menuPosition(event.currentTarget.getBoundingClientRect(), 220, 100));
                setOpenAddTabMenu((open) => !open);
              } else {
                void runWithSaveState(() => onAddTab()).catch(() => undefined);
              }
            }}
            disabled={addTabDisabled}
            aria-label={addTabLabel}
            title={addTabLabel}
            className="nt-control h-6 w-6 flex items-center justify-center rounded-lg text-fg hover:bg-surface-3 disabled:opacity-50"
          >
            <span aria-hidden="true" className="text-sm font-bold">+</span>
          </button>
        )}
      </div>
      <nav aria-label={effectiveTabMenuLabels.sheetList} className="flex-1 min-h-0 overflow-y-auto p-2">
        {renderVerticalTabs()}
      </nav>
      {/* Draggable right border resize handle */}
      <div
        onPointerDown={startSheetSidebarResize}
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-accent/40 active:bg-accent transition-colors z-10"
      />
    </aside>
  ) : null;

  const bottomTabBar = hasTabs && hasTabsAtBottom ? (
    <div
      data-grid-bottom-bar="sheets"
      data-hide-while-typing
      data-testid="bottom-tab-bar"
      className={`${fullBleed ? 'px-1 sm:px-1' : '-mx-3 px-2 sm:-mx-3 sm:px-3'} py-0.5 border-t border-border bg-surface shrink-0 flex min-w-0 items-center gap-1 z-10`}
    >
      {sheetListTrigger}
      <div
        ref={bottomSheetsScrollRef}
        onScroll={() => updateOverflowState('sheets', bottomSheetsScrollRef.current)}
        className="min-w-0 flex-1 overflow-x-auto nt-hidden-scrollbar"
      >
        <div className="flex w-max flex-nowrap items-center">{renderHorizontalTabs()}</div>
      </div>
      <HeaderOverflowControls
        labels={effectiveOverflowLabels}
        overflow={overflowState.sheets}
        onPrevious={() => scrollHeaderGroup(bottomSheetsScrollRef.current, 'previous')}
        onNext={() => scrollHeaderGroup(bottomSheetsScrollRef.current, 'next')}
      />
    </div>
  ) : null;

  const [gridBottomSlot, setGridBottomSlot] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!hasTabsAtBottom) {
      setGridBottomSlot(null);
      return;
    }
    const checkSlot = () => {
      const slot = document.getElementById('grid-bottom-tabs-slot');
      if (slot) setGridBottomSlot(slot);
    };
    checkSlot();
    const timer = setTimeout(checkSlot, 0);
    return () => clearTimeout(timer);
  }, [hasTabsAtBottom, tabs]);

  const bottomTabBarNode = hasTabs && hasTabsAtBottom ? (
    gridBottomSlot ? createPortal(bottomTabBar, gridBottomSlot) : bottomTabBar
  ) : null;

  // Cards, and controls placed at the top, share the sheet names' row: the names scroll rather than wrap.
  const cardsBesideSheets = hasTabsInTopBar && (effectiveCards !== undefined || tabButtons !== null);
  const splitGroupClass = (group: 'sheets' | 'cards' | 'buttons') => {
    if (cardsBesideSheets) {
      if (group === 'sheets') return 'min-w-[6rem] flex-1';
      if (group === 'cards') return 'min-w-0 max-w-[60%] shrink';
      // Only the sheet names scroll: the controls keep their full width.
      return 'shrink-0';
    }
    if (headerRowMode === 'all') return group === 'sheets' ? 'min-w-0 flex-1' : 'min-w-0 max-w-full shrink-0';
    if (group === 'sheets') return 'min-w-0 w-full';
    if (headerRowMode === 'cards-buttons') return 'min-w-0 max-w-full shrink-0';
    return 'min-w-0 w-full';
  };

  const pageFrameClass = fullBleed
    ? `-mt-2 -mx-3 flex h-[calc(100dvh-var(--header-h)-1px)] min-h-0 min-w-0 w-[calc(100%+1.5rem)] flex-1 flex-col overflow-hidden sm:-mx-6 sm:w-[calc(100%+3rem)] ${className}`
    : `min-w-0 w-full ${className}`;

  const summaryCardsSidebar = summaryCardsVisible && summaryCardsPlacement === 'sidebar' && (
    (summaryCards && summaryCards.length > 0) || onAddSummaryCard
  ) ? (
    <aside
      data-testid="right-cards-sidebar"
      data-hide-while-typing
      style={{ width: `${cardsSidebarWidth}px` }}
      className="relative shrink-0 self-stretch select-none border-l border-border bg-surface-2 flex flex-col min-h-0 p-2 overflow-y-auto"
    >
      <div
        onPointerDown={startCardsSidebarResize}
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-accent/40 active:bg-accent transition-colors z-10"
      />
      <div className="flex items-center justify-between pb-2 border-b border-border mb-2">
        <span className="text-xs font-semibold text-text-muted">{t('summaryCards.title')}</span>
      </div>
      <GridSummaryCards
        cards={summaryCards ?? []}
        onAddCard={onAddSummaryCard}
        addCardLabel={addSummaryCardLabel ?? t('summaryCards.addCard')}
        vertical={true}
      />
    </aside>
  ) : null;

  const bottomSummaryCards = summaryCardsVisible && summaryCardsPlacement === 'bottom' && (
    (summaryCards && summaryCards.length > 0) || onAddSummaryCard
  ) ? (
    <div
      data-testid="bottom-cards-bar"
      data-hide-while-typing
      className={`${fullBleed ? 'px-1 sm:px-1' : '-mx-3 px-2 sm:-mx-3 sm:px-3'} py-1 border-t border-border bg-surface-2 flex items-center gap-2 overflow-x-auto nt-hidden-scrollbar shrink-0 z-10`}
    >
      <GridSummaryCards
        cards={summaryCards ?? []}
        onAddCard={onAddSummaryCard}
        addCardLabel={addSummaryCardLabel ?? t('summaryCards.addCard')}
      />
    </div>
  ) : null;

  const topBelowSummaryCards = summaryCardsVisible && summaryCardsPlacement === 'topBelow' && (
    (summaryCards && summaryCards.length > 0) || onAddSummaryCard
  ) ? (
    <div
      data-testid="top-below-cards-bar"
      data-hide-while-typing
      className={`${fullBleed ? 'px-1 sm:px-1' : '-mx-3 px-2 sm:-mx-3 sm:px-3'} py-1 border-b border-border flex items-center gap-2 overflow-x-auto nt-hidden-scrollbar shrink-0 z-10`}
    >
      <GridSummaryCards
        cards={summaryCards ?? []}
        onAddCard={onAddSummaryCard}
        addCardLabel={addSummaryCardLabel ?? t('summaryCards.addCard')}
      />
    </div>
  ) : null;

  const movedControls = controlsShown && !hoistButtons && controlsPlacement !== 'top' ? controlsPlacement : null;
  const controlsBarClass = `${fullBleed ? 'px-1 sm:px-1' : '-mx-3 px-2 sm:-mx-3 sm:px-3'} py-1 flex items-center justify-end gap-2 overflow-x-auto nt-hidden-scrollbar shrink-0 z-10`;
  const topBelowControls = movedControls === 'topBelow' ? (
    <div data-testid="top-below-controls-bar" data-hide-while-typing className={`${controlsBarClass} border-b border-border`}>{controlsWithCaret}</div>
  ) : null;
  const bottomControls = movedControls === 'bottom' ? (
    <div data-testid="bottom-controls-bar" data-hide-while-typing className={`${controlsBarClass} border-t border-border bg-surface-2`}>{controlsWithCaret}</div>
  ) : null;
  const controlsSidebar = movedControls === 'sidebar' ? (
    <aside
      data-testid="right-controls-sidebar"
      data-hide-while-typing
      className="relative flex w-64 shrink-0 flex-col items-stretch gap-2 self-stretch overflow-y-auto border-l border-border bg-surface-2 p-2 min-h-0"
    >
      <div className="flex flex-wrap items-center gap-2 [&_*]:whitespace-nowrap">{buttons}</div>
      <div className="flex justify-end">{controlsCaret}</div>
    </aside>
  ) : null;

  const pageContent = (
    <div data-grid-page-frame className={pageFrameClass}>
      {hoistedButtons}
      {controlsMenu}
      {hasHeader && (
        <div data-hide-while-typing className={`${fullBleed ? 'px-1 sm:px-1' : '-mx-3 px-2 sm:-mx-3 sm:px-3'} pb-0.5 border-b border-border`}>
          {hasSplitHeader ? (
            <div ref={headerLayoutRef} className={cardsBesideSheets ? 'flex min-w-0 flex-nowrap items-center gap-x-2' : headerRowMode === 'all' ? 'flex min-w-0 flex-wrap items-center gap-y-0' : 'flex min-w-0 flex-col gap-y-1'}>
              {hasTabsInTopBar && (
                <div ref={sheetsGroupRef} data-grid-header-group="sheets" className={`${splitGroupClass('sheets')} flex min-w-0 items-center gap-1`}>
                  {sheetListTrigger}
                  <div ref={sheetsScrollRef} onScroll={() => updateOverflowState('sheets', sheetsScrollRef.current)} className="min-w-0 flex-1 overflow-x-auto nt-hidden-scrollbar">
                    <div className="flex w-max flex-nowrap items-center">{renderHorizontalTabs()}</div>
                  </div>
                  <HeaderOverflowControls labels={effectiveOverflowLabels} overflow={overflowState.sheets} onPrevious={() => scrollHeaderGroup(sheetsScrollRef.current, 'previous')} onNext={() => scrollHeaderGroup(sheetsScrollRef.current, 'next')} />
                </div>
              )}
              {(() => {
                const cardsNode = effectiveCards !== undefined ? (
                  <div ref={cardsGroupRef} data-grid-header-group="cards" className={`${hasTabsInTopBar ? splitGroupClass('cards') : 'min-w-0 flex-1'} flex min-w-0 items-center gap-1`}>
                    <div ref={cardsScrollRef} onScroll={() => updateOverflowState('cards', cardsScrollRef.current)} className="min-w-0 flex-1 overflow-x-auto nt-hidden-scrollbar">
                      <div className="flex w-max min-w-0 flex-nowrap items-center justify-start gap-2 md:min-w-full md:justify-end">{effectiveCards ?? header}</div>
                    </div>
                    <HeaderOverflowControls labels={effectiveOverflowLabels} overflow={overflowState.cards} onPrevious={() => scrollHeaderGroup(cardsScrollRef.current, 'previous')} onNext={() => scrollHeaderGroup(cardsScrollRef.current, 'next')} />
                  </div>
                ) : null;
                const buttonsNode = tabButtons !== null ? (
                  <div ref={buttonsGroupRef} data-grid-header-group="buttons" className={`${hasTabsInTopBar ? splitGroupClass('buttons') : 'min-w-0 shrink-0'} ml-auto flex min-w-0 items-center justify-end gap-1`}>
                    <div ref={buttonsScrollRef} onScroll={() => updateOverflowState('buttons', buttonsScrollRef.current)} className="min-w-0 flex-1 overflow-x-auto nt-hidden-scrollbar">
                      <div className="flex w-max min-w-full flex-nowrap items-center justify-end gap-2">{tabButtons}</div>
                    </div>
                    <HeaderOverflowControls labels={effectiveOverflowLabels} overflow={overflowState.buttons} onPrevious={() => scrollHeaderGroup(buttonsScrollRef.current, 'previous')} onNext={() => scrollHeaderGroup(buttonsScrollRef.current, 'next')} />
                  </div>
                ) : null;
                if (headerRowMode === 'cards-buttons') {
                  return <div className="flex min-w-0 flex-wrap items-center gap-y-0">{cardsNode}{buttonsNode}</div>;
                }
                return <>{cardsNode}{buttonsNode}</>;
              })()}
            </div>
          ) : header && hasTabsInTopBar ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-1">
                {sheetListTrigger}
                <div className="min-w-0 max-w-full shrink-0 overflow-x-auto nt-hidden-scrollbar">
                  <div className="flex w-max flex-nowrap items-center">{renderHorizontalTabs()}</div>
                </div>
              </div>
              <div className="flex min-w-max flex-wrap items-center justify-end gap-2">
                {header}
              </div>
            </div>
          ) : header ? (
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
              {header}
              {!hasTabsInTopBar && hasTabs ? null : addTab}
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 items-center">
                {hasTabsInTopBar ? (
                  <>
                    {sheetListTrigger}
                    <div className="min-w-0 max-w-full shrink-0 overflow-x-auto nt-hidden-scrollbar">
                      <div className="flex w-max flex-nowrap items-center">{renderHorizontalTabs()}</div>
                    </div>
                  </>
                ) : (
                  title && (
                    <span className="px-4 py-2 text-sm font-bold border-b-2 border-brand text-brand min-w-0 truncate">
                      {title}
                    </span>
                  )
                )}
                {!hasTabs && addTab}
              </div>
              {actions && (
                <div className="flex flex-wrap items-center gap-2 pb-1.5">
                  {actions}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {topBelowSummaryCards}
      {topBelowControls}

      {error && (
        <p
          role="alert"
          className="mx-5 mb-4 rounded-xl border border-danger bg-danger-soft p-3 text-sm text-danger"
        >
          {error}
        </p>
      )}

      <div
        className={
          (fullBleed
            ? `flex min-h-0 w-full flex-1 flex-row overflow-hidden`
            : 'flex min-h-0 w-full flex-1 flex-row'
          ).trim()
        }
      >
        {sheetSidebar}
        <div
          className={
            (fullBleed
              ? `flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden ${contentClassName}`
              : `flex min-h-0 min-w-0 w-full flex-1 flex-col ${contentClassName}`
            ).trim()
          }
        >
          <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
            {children}
          </div>
          {bottomSummaryCards}
          {bottomControls}
          {!gridBottomSlot && bottomTabBarNode}
          {gridBottomSlot && bottomTabBarNode}
        </div>
        {summaryCardsSidebar}
        {controlsSidebar}
        {rightSidebar}
      </div>
      {tabSubmenu}
    </div>
  );

  return pageContent;
}

export function GridPageLayout(props: GridPageLayoutProps) {
  const existingContext = useOptionalSummaryCards();
  if (existingContext) {
    return <GridPageLayoutInner {...props} />;
  }
  return (
    <SummaryCardsProvider>
      <GridPageLayoutInner {...props} />
    </SummaryCardsProvider>
  );
}
